import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import TruncatedText from "../../Components/TruncatedText";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE, resolveImageUrl } from "../../config/api";
import Pagination from "../../Components/Pagination";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { parseApiError } from "../../utils/parseApiError";

const PAGE_SIZE = 25;

interface Store {
  store_id: number;
  user_id: number;
  store_name: string;
  store_description: string;
  store_image: string;
  store_image_url?: string;
  address?: { city?: string; address1?: string; country?: string; latitude?: string; longitude?: string } | null;
  max_advance_booking_days?: number;
  channel_type?: string;
  created_at: string;
  updated_at: string;
}

interface EditStore {
  store_id?: number;
  user_id?: string;
  store_name: string;
  store_description: string;
  store_image: string;
  store_image_url?: string;
  city: string;
  max_advance_booking_days: string;
}

interface Admin {
  user_id: number;
  user_first_name: string;
  user_last_name: string;
}

interface DeliveryArea {
  delivery_area_id: number;
  store_id: number;
  city: string;
}

const StorePage = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedStores, setSelectedStores] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editStore, setEditStore] = useState<EditStore>({
    user_id: "",
    store_name: "",
    store_description: "",
    store_image: "",
    city: "",
    max_advance_booking_days: "3",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deliveryAreas, setDeliveryAreas] = useState<DeliveryArea[]>([]);
  const [newAreaCity, setNewAreaCity] = useState("");
  const [isAreaLoading, setIsAreaLoading] = useState(false);
  const [channelType, setChannelType] = useState("pickup_and_delivery");
  const [isChannelLoading, setIsChannelLoading] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    if (permissions.isOwner) fetchAdmins();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const fetchStores = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/store?${params.toString()}`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setStores(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch stores. Status:", response.status);
        console.error("Error response:", errorText);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  }, [cookies.auth, page, debouncedSearchTerm]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Only the Owner creates stores, and must associate the new store with an existing Admin.
  const fetchAdmins = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/user/users?user_type=A&page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        const list = data.success && Array.isArray(data.users) ? data.users : (Array.isArray(data) ? data : []);
        setAdmins(list);
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  }, [cookies.auth]);
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64String = result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Owner-only delivery-area coverage — Admin can view a store's own areas
  // but not edit them, so this is only fetched here (Update flow); the Add
  // flow has no store_id yet to attach areas to.
  const fetchDeliveryAreas = useCallback(async (storeId: number) => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/store/${storeId}/delivery-area`, {
        method: 'GET'
      }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setDeliveryAreas(Array.isArray(data?.areas) ? data.areas : []);
      }
    } catch (error) {
      console.error("Error fetching delivery areas:", error);
    }
  }, [cookies.auth]);

  const handleAdd = () => {
    setEditStore({
      user_id: "",
      store_name: "",
      store_description: "",
      store_image: "",
      city: "",
      max_advance_booking_days: "3",
    });
    setImageFile(null);
    setChannelType("pickup_and_delivery");
    setShowAddModal(true);
  };

  const handleUpdate = () => {
    if (selectedStores.length !== 1) {
      alert("Please select exactly one store to update");
      return;
    }

    const storeToEdit = stores.find(store => store.store_id === selectedStores[0]);
    if (storeToEdit) {
      setEditStore({
        store_id: storeToEdit.store_id,
        user_id: storeToEdit.user_id.toString(),
        store_name: storeToEdit.store_name,
        store_description: storeToEdit.store_description,
        store_image: storeToEdit.store_image,
        store_image_url: storeToEdit.store_image_url,
        city: storeToEdit.address?.city || "",
        max_advance_booking_days: String(storeToEdit.max_advance_booking_days ?? 3),
      });
      setImageFile(null);
      setDeliveryAreas([]);
      setNewAreaCity("");
      setChannelType(storeToEdit.channel_type || "pickup_and_delivery");
      if (permissions.isOwner) fetchDeliveryAreas(storeToEdit.store_id);
      setShowEditModal(true);
    }
  };

  // Owner-only, saved immediately on change (same pattern as the
  // delivery-area add/remove above — a separate endpoint, not bundled into
  // handleSave's payload, since editStore never reads this field). See
  // docs/store-channel-access-control-plan.md, Step 3.
  const handleChangeChannelType = async (newChannelType: string) => {
    if (!editStore.store_id) return;
    const previous = channelType;
    setChannelType(newChannelType);
    try {
      setIsChannelLoading(true);
      const response = await authenticatedFetch(`${API_BASE}/store/${editStore.store_id}/channel-type`, {
        method: "PUT",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel_type: newChannelType }),
      }, cookies.auth);
      if (!response.ok) {
        throw new Error(await parseApiError(response, "Failed to update channel type"));
      }
      await fetchStores();
      snack.onResponse({ message: "Channel type updated", status: 200 });
    } catch (error) {
      setChannelType(previous);
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to update channel type", status: 400 });
    } finally {
      setIsChannelLoading(false);
    }
  };

  const handleAddDeliveryArea = async () => {
    const city = newAreaCity.trim();
    if (!city || !editStore.store_id) return;
    try {
      setIsAreaLoading(true);
      const response = await authenticatedFetch(`${API_BASE}/store/${editStore.store_id}/delivery-area`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city }),
      }, cookies.auth);
      if (!response.ok) {
        throw new Error(await parseApiError(response, "Failed to add delivery area"));
      }
      setNewAreaCity("");
      await fetchDeliveryAreas(editStore.store_id);
      snack.onResponse({ message: "Delivery area added", status: 200 });
    } catch (error) {
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to add delivery area", status: 400 });
    } finally {
      setIsAreaLoading(false);
    }
  };

  const handleRemoveDeliveryArea = async (areaId: number) => {
    if (!editStore.store_id) return;
    try {
      setIsAreaLoading(true);
      const response = await authenticatedFetch(`${API_BASE}/store/${editStore.store_id}/delivery-area/${areaId}`, {
        method: "DELETE",
      }, cookies.auth);
      if (!response.ok) {
        throw new Error(await parseApiError(response, "Failed to remove delivery area"));
      }
      await fetchDeliveryAreas(editStore.store_id);
      snack.onResponse({ message: "Delivery area removed", status: 200 });
    } catch (error) {
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to remove delivery area", status: 400 });
    } finally {
      setIsAreaLoading(false);
    }
  };

  const handleDelete = () => {
    if (selectedStores.length === 0) {
      alert("Please select at least one store to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      
      for (const storeId of selectedStores) {
        const response = await authenticatedFetch(`${API_BASE}/store/${storeId}`, {
          method: "DELETE"
        }, cookies.auth);
        if (!response.ok) {
          throw new Error(await parseApiError(response, `Failed to delete store ${storeId}`));
        }
      }
      const count = selectedStores.length;
      setSelectedStores([]);
      setShowDeleteConfirm(false);
      await fetchStores();
      snack.onResponse({ message: `${count} store${count === 1 ? "" : "s"} deleted`, status: 200 });
    } catch (error) {
      console.error("Error deleting stores:", error);
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to delete store(s)", status: 400 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    if (!isEdit && !editStore.user_id) {
      snack.onResponse({ message: "Please select which Admin this store belongs to.", status: 400 });
      return;
    }

    try {
      setIsLoading(true);
      let imageBase64: string | null = null;

      if (imageFile) {
        try {
          imageBase64 = await convertFileToBase64(imageFile!);
        } catch (error) {
          console.error('Error converting image to base64:', error);
          snack.onResponse({ message: "Failed to read the selected image", status: 400 });
          return;
        }
      }

      const payload: Record<string, unknown> = {
        store_name: editStore.store_name,
        store_description: editStore.store_description,
        store_image: imageBase64 || editStore.store_image,
      };
      if (!isEdit) {
        payload.user_id = editStore.user_id;
      }
      if (editStore.city.trim()) {
        payload.address = { city: editStore.city.trim() };
      }
      if (isEdit) {
        payload.max_advance_booking_days = editStore.max_advance_booking_days;
      }

      const url = isEdit
        ? `${API_BASE}/store/${editStore.store_id}`
        : `${API_BASE}/store`;
      
      const method = isEdit ? "PUT" : "POST";
      
      const response = await authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': cookies.auth?.user_id || 1
        },
        body: JSON.stringify(payload),
      }, cookies.auth);

      if (!response.ok) {
        throw new Error(await parseApiError(response, "Failed to save store"));
      }

      setShowEditModal(false);
      setShowAddModal(false);
      setEditStore({
        user_id: "",
        store_name: "",
        store_description: "",
        store_image: "",
        city: "",
        max_advance_booking_days: "3",
      });
      setImageFile(null);
      setDeliveryAreas([]);
      setNewAreaCity("");
      setChannelType("pickup_and_delivery");
      setSelectedStores([]); // Clear selection after successful update
      await fetchStores();
      snack.onResponse({ message: isEdit ? "Store updated" : "Store created", status: 200 });
    } catch (error) {
      console.error("Error saving store:", error);
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to save store", status: 400 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedStores(stores.map(store => store.store_id));
    } else {
      setSelectedStores([]);
    }
  };

  const handleSelectStore = (storeId: number, checked: boolean) => {
    if (checked) {
      // Single selection for update - clear all others and select only this one
      setSelectedStores([storeId]);
    } else {
      setSelectedStores(selectedStores.filter(id => id !== storeId));
    }
  };

  const filteredStores = stores;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };



  return (
    <div className={style.wrapper}>
      {/* Header Section */}
      <div className={style.header}>
        <h1 className={style.title}>Store</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by store name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canAdd && (
            <div className={style.actionButtons}>
              {permissions.isOwner && (
                <button className={style.actionBtn} onClick={handleAdd}>
                  Add
                </button>
              )}
              <button className={style.actionBtn} onClick={handleUpdate}>
                Update
              </button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>
                Delete Store
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canEdit && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedStores.length === stores.length && stores.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Store ID</th>
              <th>User ID</th>
              <th>Store Name</th>
              <th>Store Description</th>
              <th>City</th>
              <th>Image</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {filteredStores.map((store) => (
              <tr key={store.store_id} className={style.tableRow}>
                {permissions.canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedStores.includes(store.store_id)}
                      onChange={(e) => handleSelectStore(store.store_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>{store.store_id}</td>
                <td>{store.user_id}</td>
                <td>{store.store_name}</td>
                <td className={style.descriptionCell}>
                  <TruncatedText text={store.store_description} maxLength={50} />
                </td>
                <td>{store.address?.city || '—'}</td>
                <td>
                  <img
                    src={store.store_image_url
                      ? resolveImageUrl(store.store_image_url)
                      : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43NjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }
                    alt={store.store_name}
                    className={style.storeImage}
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43NjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }}
                  />
                </td>
                <td>{formatDate(store.created_at)}</td>
                <td>{formatDate(store.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>{showAddModal ? "Add New Store" : "Update Store"}</h2>
            <div className={style.modalContent}>
              {showAddModal && (
                <select
                  value={editStore.user_id || ""}
                  onChange={(e) => setEditStore({ ...editStore, user_id: e.target.value })}
                  className={style.modalInput}
                  required
                >
                  <option value="">Assign to Admin *</option>
                  {admins.map((admin) => (
                    <option key={admin.user_id} value={admin.user_id}>
                      {admin.user_first_name} {admin.user_last_name}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="text"
                placeholder="Store Name"
                value={editStore.store_name}
                onChange={(e) => setEditStore({...editStore, store_name: e.target.value})}
                className={style.modalInput}
              />
              <textarea
                placeholder="Store Description"
                value={editStore.store_description}
                onChange={(e) => setEditStore({...editStore, store_description: e.target.value})}
                className={style.modalInput}
              />
              <input
                type="text"
                placeholder="City *"
                value={editStore.city}
                onChange={(e) => setEditStore({...editStore, city: e.target.value})}
                className={style.modalInput}
                required
              />
              <input
                type="file"
                onChange={handleImageChange}
                className={style.modalInput}
                accept="image/*"
              />
                                            {editStore.store_image && !imageFile && (
                 <img
                   src={resolveImageUrl(editStore.store_image_url)}
                   alt="Current store"
                   className={style.currentImage}
                   onError={(e) => {
                     e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43NjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                   }}
                 />
               )}

              {showEditModal && (
                <>
                  <label className={style.modalLabel}>
                    Advance booking window (days)
                    <input
                      type="number"
                      min={1}
                      placeholder="Advance booking window (days)"
                      value={editStore.max_advance_booking_days}
                      onChange={(e) => setEditStore({ ...editStore, max_advance_booking_days: e.target.value })}
                      className={style.modalInput}
                    />
                  </label>

                  {permissions.isOwner && (
                    <label className={style.modalLabel}>
                      Channel type
                      <select
                        value={channelType}
                        onChange={(e) => handleChangeChannelType(e.target.value)}
                        disabled={isChannelLoading}
                        className={style.modalInput}
                      >
                        <option value="pos_only">POS only</option>
                        <option value="pickup_only">POS + mobile pickup</option>
                        <option value="pickup_and_delivery">POS + mobile pickup &amp; delivery</option>
                      </select>
                    </label>
                  )}

                  {permissions.isOwner && (
                    <div className={style.deliveryAreasSection}>
                      <label className={style.modalLabel}>Delivery areas (cities)</label>
                      <div className={style.deliveryAreaChips}>
                        {deliveryAreas.length === 0 && (
                          <span className={style.deliveryAreaHint}>
                            No delivery areas set yet — this store matches its own city by default.
                          </span>
                        )}
                        {deliveryAreas.map((area) => (
                          <span key={area.delivery_area_id} className={style.deliveryAreaChip}>
                            {area.city}
                            <button
                              type="button"
                              onClick={() => handleRemoveDeliveryArea(area.delivery_area_id)}
                              disabled={isAreaLoading}
                              className={style.deliveryAreaRemoveBtn}
                              aria-label={`Remove ${area.city}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className={style.deliveryAreaAddRow}>
                        <input
                          type="text"
                          placeholder="Add a city"
                          value={newAreaCity}
                          onChange={(e) => setNewAreaCity(e.target.value)}
                          className={style.modalInput}
                        />
                        <button
                          type="button"
                          onClick={handleAddDeliveryArea}
                          disabled={isAreaLoading || !newAreaCity.trim()}
                          className={style.actionBtn}
                        >
                          Add Area
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className={style.modalButtons}>
                <button
                  onClick={() => handleSave(showEditModal)}
                  className={style.saveBtn}
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : (showAddModal ? "Add Store" : "Update Store")}
                </button>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className={style.cancelBtn}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete {selectedStores.length} selected store(s)?</p>
            <div className={style.modalButtons}>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? "Deleting..." : "Yes, Delete" }
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={style.cancelBtn}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorePage;
